import classnames from 'classnames';
import Dropzone from 'react-dropzone';
import React, { useEffect, useState } from 'react';
import { Translation, useTranslation } from 'react-i18next';
import LoadingCircle from 'components/ui/LoadingCircle';
import ShipmentWebAPIUtils from 'utils/ShipmentWebAPIUtils';
import { STOP_COMPLETION_FILE_TYPES } from 'config/accepted-file-types';
import { sluggify } from 'utils/localization/utils';
import {
  PROOF_OF_DELIVERY,
  PROOF_OF_PICKUP,
  CUSTOM,
  documentTypeNameMap,
  shipperViewableDocTypesMap,
} from 'config/documents';
import SessionStore from '../../../stores/SessionStore';
import PlusIcon from '../../v2/icons/PlusIcon';
import DocumentPreview from '../shipments/stops/DocumentPreview';
import { InvalidPDFs } from './InvalidPDFs';
import { showToast } from '../../../stores/ToastStore';
import { areSomeFilesEmpty } from '../../../utils/files/files';

const green = '#21B88E';
const red = '#a94442';

export function PodUploader({
  shipment,
  stop,
  meta,
  intentRequirements,
  error,
  onUpload,
  onUploadFailed,
  handleComplete,
  onProcessing,
}) {
  if (!SessionStore.isAdmin() && !SessionStore.isCarrier()) {
    return null;
  }

  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [uploadingRaw, setUploadingRaw] = useState(false);
  const [validating, setValidating] = useState(false);
  const [invalidPDFs, setInvalidPDFs] = useState([]);

  const uploadDocuments = async (validFiles, signatory) => {
    const response = await ShipmentWebAPIUtils.uploadStopDocuments(
      shipment,
      stop,
      validFiles,
      signatory,
    ).catch((e) => {
      throw e;
    });

    return response;
  };

  const uploadFailed = async (error) => {
    if (!error._limit) {
      showToast(
        t('DOCUMENT_UPLOAD_FAILED_RETRY'),
        t('DOCUMENT_UPLOAD_FAILED_WITH_ERROR', { error }),
        'error',
      );
    }

    return onUploadFailed && onUploadFailed(shipment, stop, error);
  };

  const handleUploadRaw = async (validFiles, type, signatory = '') => {
    try {
      setUploadingRaw(true);

      const userVisibility = [];

      if (shipperViewableDocTypesMap[type]) {
        const docType = shipperViewableDocTypesMap[type];

        if (docType.carrierCanUpload) {
          userVisibility.push('Carrier');
          userVisibility.push('Driver');
        }

        if (docType.shipperCanUpload) {
          userVisibility.push('Shipper');
        }
      } else {
        userVisibility.push('Shipper', 'Carrier', 'Driver');
      }

      const documentsData = await Promise.all(
        validFiles.map(async (file) => {
          const filename =
            type === CUSTOM.value ? file.name : documentTypeNameMap[type];

          return {
            type,
            name: filename,
            signatory,
            content: file,
            contentType: file.type,
            size: file.size,
          };
        }),
      );

      const uploadedDocument = await uploadDocuments(
        documentsData,
        signatory,
      ).catch((err) => {
        throw err;
      });
      return onUpload(shipment, stop, type, uploadedDocument, userVisibility);
    } catch (e) {
      return uploadFailed(e);
    } finally {
      setUploadingRaw(false);
    }
  };

  const validationFailed = async (e) => {
    showToast(
      <Translation>
        {(t) => <p data-testid="p-b48fdfb4">{t('DOCUMENT_VALIDATION_FAILED')}</p>}
      </Translation>,
      <Translation>
        {(t) => <p data-testid="p-4dcfcab7">{t('DOCUMENT_VALIDATION_FAILED')}</p>}
      </Translation>,
      'error',
    );

    setValidating(false);
    setInvalidPDFs([]);
  };

  const onValidate = async (validFiles) => {
    setFiles([...files, ...validFiles]);
    const type =
      stop.action === 'pickup' || stop.action === 'pickup_empty'
        ? PROOF_OF_PICKUP.value
        : PROOF_OF_DELIVERY.value;

    return handleUploadRaw(validFiles, type);
  };

  const handleValidate = async (filesDropped) => {
    try {
      setValidating(true);
      setInvalidPDFs([]);

      if (!filesDropped.length) {
        return validationFailed(files, new Error('No valid files'));
      }
      if (areSomeFilesEmpty(filesDropped)) {
        return validationFailed(files, new Error('Some files are empty'));
      }
      const invalidPDFs = [];
      const validFiles = filesDropped.filter(
        (file) => !invalidPDFs.includes(file.name),
      );

      setValidating(false);
      setInvalidPDFs(invalidPDFs);

      return onValidate(validFiles, invalidPDFs);
    } catch (e) {
      return validationFailed(e);
    }
  };

  useEffect(() => {
    onProcessing?.(validating || uploadingRaw);
  }, [validating, uploadingRaw]);

  const isPODRequired = () => {
    if (stop && shipment) {
      return (
        stop.missing_docs &&
        stop.action === 'deliver' &&
        (meta?.signatory === '' || !meta?.signatory) &&
        (!files || files.length === 0)
      );
    }

    return false;
  };

  const className = classnames({
    border: true,
    'border-solid': true,
    'border-red': error?.file,
    'px-4': true,
    'py-2': true,
  });
  const borderColor = error?.file ? red : green;
  const hasPendingUploads = files.length > 0;
  const suffix = hasPendingUploads ? 'Additional Files' : 'File';
  const label = `Click to add ${suffix}`;

  return (
    (<div data-testid="div-09f5d06f">
      {hasPendingUploads && (
        <div data-testid="div-e984dfe7" className="grid-3-x-3 mb-4">
          {files.map((f, idx) => (
            <div data-testid={`div-1e84aec7-${idx}`} key={idx} className={className}>
              <DocumentPreview
                url={f.preview}
                type={f.type}
                displayInGridView
                renderDownloadableImage
              />
            </div>
          ))}
        </div>
      )}
      {(validating || uploadingRaw) && (
        <LoadingCircle dataTestId="LoadingCircle-1f910fef" text={t('PROCESSING_FILES')} />
      )}
      <InvalidPDFs invalidPDFs={invalidPDFs} />
      <div data-testid="div-4ca85eed" className="mb-4">
        <Dropzone
          style={{
            width: '100%',
            height: 100,
            padding: 10,
            borderWidth: 2,
            borderColor,
            borderStyle: 'dashed',
            borderRadius: 5,
          }}
          onDrop={handleValidate}
          multiple
          accept={STOP_COMPLETION_FILE_TYPES}
          id="stop-document-upload-zone"
        >
          <div data-testid="div-88cfb312" className="flex flex-wrap space-x-1">
            <PlusIcon />
            <span data-testid="span-f1489502">
              {t(sluggify(label))}
              {intentRequirements?.pocOptional &&
                !isPODRequired() &&
                t('OPTIONAL_WITH_BRACKETS')}
            </span>
          </div>
        </Dropzone>
      </div>
      {handleComplete && (
        <div data-testid="div-6c6dd072" className="flex justify-end">
          <button
            data-testid="button-8dc22772"
            className="button button-green"
            onClick={handleComplete}
            disabled={!hasPendingUploads || validating}>
            {validating ? 'Validating...' : 'Save Updates'}
          </button>
        </div>
      )}
    </div>)
  );
}
